import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_source-scout-4/artifacts/hca8zcbd_WhatsApp%20Image%202026-02-28%20at%2022.18.51.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });

      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // New users must replace their temporary password before accessing admin pages.
      navigate(response.data.user.must_change_password ? '/admin/change-password' : '/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Tejas Info Solutions"
            className="h-16 w-auto mx-auto mb-4"
            data-testid="login-logo"
          />
          <h1 className="text-2xl font-bold text-white uppercase tracking-wider">
            Admin Portal
          </h1>
          <p className="text-slate-400 mt-2">Sign in to manage your inventory</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-sm p-8 border border-slate-700">
          <form onSubmit={handleSubmit} data-testid="login-form">
            {location.state?.passwordChanged && (
              <div data-testid="password-change-success" className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 text-green-300 px-4 py-3 rounded-sm mb-6">
                <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Your password has been changed successfully. Please sign in using your new password.</span>
              </div>
            )}
            {/* Error Message */}
            {error && (
              <div 
                data-testid="login-error"
                className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-sm mb-6"
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="mb-6">
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  data-testid="login-email-input"
                  className="w-full bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 pl-12 pr-4 py-4 rounded-none focus:border-orange-500 focus:outline-none transition-colors"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                  className="w-full bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 pl-12 pr-12 py-4 rounded-none focus:border-orange-500 focus:outline-none transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full btn-lift bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-4 rounded-none hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <a
              href="/"
              data-testid="back-to-home-link"
              className="text-slate-400 hover:text-orange-500 transition-colors text-sm"
            >
              ← Back to Home
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-8">
          © {new Date().getFullYear()} Tejas Info Solutions. All rights reserved.
        </p>
      </div>
    </div>
  );
};
